import React, { useRef, useCallback, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PARTNER_CONFIG } from "../../config/partnerConfig";
import { useMerchantForm } from "../../hooks/useMerchantForm";
import { submitMerchantForm } from "../../services/merchantSubmissionService";
import { validateFields } from "../../utils/validateFields";
import {
  MerchantFormHeader,
  CompanySelection,
  BusinessDetails,
  DocumentsSection,
  NotesField,
  RateQuote,
  ReasonField,
  PersonalInfoFields,
  Loader,
} from "../../shared";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import "./MerchantSubmission.scss";
import { showAuthStatusMessages } from "../../utils/auth0Utils";

const MerchantSubmission = () => {
  // Get partner identifier from URL params, fallback to "default" config if none provided
  const { partner } = useParams();

  // Get partner-specific configuration based on the URL parameter
  const config = PARTNER_CONFIG[partner || "default"];

  // Refs to reset file input values after submission or reset
  const fileRef = useRef(null);

  // State to manage loading spinner during async submission
  const [isLoading, setIsLoading] = useState(false);

  // State flag to trigger UI reset for file input info display (if any)
  const [resetFileInfo, setResetFileInfo] = useState(false);

  // Message API from MessageProvider for toast notifications
  const message = useMessageApi();

  // Custom hook managing form state and input handlers
  const { state, setState, handleChange, handleSelectChange, resetForm } =
    useMerchantForm(config);

  // Check for user-not-found flags and surface messages on mount
  useEffect(() => {
    showAuthStatusMessages(message);
  }, [message]);

  // List of form fields to check if any data is present (for enabling reset button)
  const formKeys = [
    "firstName",
    "lastName",
    "businessName",
    "email",
    "phoneNumber",
    "Gateway",
    "subRep",
    "notes",
    "reason",
    "projectedVolume",
    "file",
    "document",
  ];

  /**
   * Checks if any form field contains data (excluding errors and modal states)
   * Used to toggle Reset button enable/disable state
   */
  const hasFormData = useCallback(() => {
    return formKeys.some((key) => {
      const value = state[key];
      
      // Handle file arrays (for documents)
      if (key === "file" && Array.isArray(value)) {
        return value.length > 0;
      }
      
      // Handle regular values
      return value !== null && value !== undefined && value !== "";
    });
  }, [state]);

  /**
   * Handles change events on file inputs and updates form state accordingly
   * @param {Event} event - The input change event
   * @param {string} type - Key representing which file input changed
   */
  const handleFileChange = useCallback(
    (event, type) => {
      // Handle multiple files for document uploads
      if (type === "file" && event.target.files && event.target.files.length > 0) {
        // Store the array of files for documents
        setState((prev) => ({ ...prev, [type]: Array.from(event.target.files) }));
      } else if (event.target.files && event.target.files.length > 0) {
        // For other file types (like statement), store the first file
        setState((prev) => ({ ...prev, [type]: event.target.files[0] }));
      } else {
        // Clear the file field if no files selected
        setState((prev) => ({ ...prev, [type]: null }));
      }
    },
    [setState]
  );

  /**
   * Handles form submission:
   * - Validates inputs
   * - Shows loader while submitting
   * - Submits form data
   * - Resets form and file inputs on success
   * - Handles errors gracefully
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate fields based on partner-specific config rules
    const errors = validateFields(state, config);
    if (Object.keys(errors).length > 0) {
      setState((prev) => ({ ...prev, errors }));
      return;
    }

    setIsLoading(true);

    try {
      await submitMerchantForm(state, config, message);
      resetAllFilesAndForm();
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Custom handler to reset the form fields and file inputs.
   * Shows toast notification on reset.
   * Prevents reset if form is already empty.
   */
  const handleResetForm = () => {
    // Disable reset if no data entered
    if (!hasFormData()) return;
    resetAllFilesAndForm();
    message.info("Form has been reset!", 3);
  };

  /**
   * Resets the form state and clears file inputs.
   * Also triggers file info reset flag to update UI.
   */
  const resetAllFilesAndForm = () => {
    resetForm();

    if (fileRef.current) fileRef.current.value = "";

    setResetFileInfo(true);
    setTimeout(() => setResetFileInfo(false), 100);
  };

  // If no valid config found for the partner, show error message
  if (!config) {
    return (
      <div className="error-message">
        Invalid partner configuration. Please check the URL.
      </div>
    );
  }

  return (
    <>
      {/* Loader overlay while async submission is in progress */}
      <Loader isLoading={isLoading} />

      {/* Header of the Merchant Submission Form */}
      <MerchantFormHeader />

      <section>
        <div className="container">
          <div className="new-merchant-submission">
            <h1 className="text-center mb-5 form-heading">
              New Merchant Submission
            </h1>

            <form
              className="needs-validation"
              onSubmit={handleSubmit}
              noValidate
            >
              {/* Personal Info Section */}
              <PersonalInfoFields state={state} handleChange={handleChange} />

              {/* Conditionally render Company Selection if enabled */}
              {config.hasCompanySelect && (
                <CompanySelection state={state} handleChange={handleChange} />
              )}

              {/* Business Details Section */}
              <BusinessDetails
                config={config}
                state={state}
                handleChange={handleChange}
                handleSelectChange={handleSelectChange}
              />

              {/* Conditionally render Documents Section based on config */}
              {!config.hasSimpleForm && (
                <DocumentsSection
                  config={config}
                  fileRef={fileRef}
                  handleFileChange={handleFileChange}
                  resetFileInfo={resetFileInfo}
                />
              )}

              {/* Reason Field */}
              <ReasonField state={state} handleChange={handleChange} />

              {/* Rate Quote Section if enabled */}
              {config.hasRateQuote && (
                <RateQuote state={state} handleChange={handleChange} />
              )}

              {/* Notes Field and Action Buttons */}
              <div className="row mt-3">
                <div className="col-12">
                  <NotesField state={state} handleChange={handleChange} />
                </div>

                <div className="col-12 pt-3 text-end">
                  {/* Reset Button disabled if no form data */}
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={handleResetForm}
                    disabled={!hasFormData()}
                  >
                    Reset Form
                  </button>

                  {/* Submit Button disabled during submission */}
                  <button
                    type="submit"
                    className="btn merchant-submit-btn"
                    disabled={isLoading}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default MerchantSubmission;
